/**
 * RBAC observability (monitoring only — does not affect authorization).
 * No PII is recorded.
 */

export interface RBACMonitorSnapshot {
  permissionsLoaded: number;
  permissionsRefresh: number;
  permissionsLoadFailures: number;
  api403Responses: number;
  routeDenied: number;
  permissionBasedRouteDenied: number;
  routeListGuardDenied: number;
  lastRole: string | null;
  lastFeatureCount: number;
  permissionChecks: number;
  missingPermissionKeys: number;
}

const state: RBACMonitorSnapshot = {
  permissionsLoaded: 0,
  permissionsRefresh: 0,
  permissionsLoadFailures: 0,
  api403Responses: 0,
  routeDenied: 0,
  permissionBasedRouteDenied: 0,
  routeListGuardDenied: 0,
  lastRole: null,
  lastFeatureCount: 0,
  permissionChecks: 0,
  missingPermissionKeys: 0,
};

const isDev = import.meta.env.DEV;
const seenMissingKeys = new Set<string>();

function logDevMonitor(label: string, extra?: Record<string, string | number | null>) {
  if (!isDev) {
    return;
  }
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[RBAC Monitor] ${label}`);
  // eslint-disable-next-line no-console
  console.table({ ...getSnapshot(), ...extra });
  // eslint-disable-next-line no-console
  console.groupEnd();
}

export function getSnapshot(): RBACMonitorSnapshot {
  return { ...state };
}

export function recordPermissionsLoaded(role: string | null, featureCount: number): void {
  state.permissionsLoaded += 1;
  state.lastRole = role;
  state.lastFeatureCount = featureCount;
  logDevMonitor('Permissions loaded', { role, featureCount });
}

export function recordPermissionsRefresh(): void {
  state.permissionsRefresh += 1;
  logDevMonitor('Permissions refresh');
}

export function recordPermissionsLoadFailure(): void {
  state.permissionsLoadFailures += 1;
  logDevMonitor('Permissions load failed');
}

export function recordApi403(path: string, method: string): void {
  state.api403Responses += 1;
  logDevMonitor('API 403', { path, method });
}

export function recordPermissionBasedRouteDenied(
  feature: string,
  action: string,
  path: string
): void {
  state.routeDenied += 1;
  state.permissionBasedRouteDenied += 1;
  logDevMonitor('PermissionBasedRoute denied', { feature, action, path });
}

export function recordRouteListGuardDenied(feature: string, action: string, path: string): void {
  state.routeDenied += 1;
  state.routeListGuardDenied += 1;
  logDevMonitor('RouteListPermissionGuard denied', { feature, action, path });
}

export function recordUnmappedRouteDenied(path: string): void {
  state.routeDenied += 1;
  state.routeListGuardDenied += 1;
  logDevMonitor('RouteListPermissionGuard denied unmapped path', { path });
}

export function recordPermissionCheck(): void {
  state.permissionChecks += 1;
}

export function recordMissingPermissionKey(feature: string): void {
  if (seenMissingKeys.has(feature)) {
    return;
  }
  seenMissingKeys.add(feature);
  state.missingPermissionKeys += 1;
  logDevMonitor('Missing permission key in payload', { feature });
}

/** Development-only summary for console debugging. */
export function printRBACMonitorSummary(): void {
  if (!isDev) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info('[RBAC Monitor] Summary', getSnapshot());
}

export function resetRBACMonitorForTests(): void {
  state.permissionsLoaded = 0;
  state.permissionsRefresh = 0;
  state.permissionsLoadFailures = 0;
  state.api403Responses = 0;
  state.routeDenied = 0;
  state.permissionBasedRouteDenied = 0;
  state.routeListGuardDenied = 0;
  state.lastRole = null;
  state.lastFeatureCount = 0;
  state.permissionChecks = 0;
  state.missingPermissionKeys = 0;
  seenMissingKeys.clear();
}
